/**
 * Session Store - Zustand Implementation
 *
 * Migrated from custom SessionStore to use Zustand
 * Benefits:
 * - Less boilerplate code
 * - Built-in DevTools support
 * - Better performance with automatic batching
 * - TypeScript-first design
 */

import { useEffect } from 'react';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { getSessionToken, removeSessionToken, type SessionUser, type APIResponse, type SessionResponse } from '../utils/auth';

// Auth API interface - to be provided by apps
let authAPIProvider: { getSession: () => Promise<APIResponse<SessionResponse>> } | null = null;

export function setAuthAPI(api: { getSession: () => Promise<APIResponse<SessionResponse>> }) {
  authAPIProvider = api;
}

interface SessionState {
  // State
  user: SessionUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  hasCheckedSession: boolean;
  error: Error | null;
  lastFetchTime: number;

  // Actions
  fetchSession: (force?: boolean) => Promise<void>;
  clearSession: () => void;
  setSession: (user: SessionUser | null) => void;
  _setLoading: (loading: boolean) => void;
  _setError: (error: Error | null) => void;
}

const CACHE_DURATION = 60000; // 1 minute cache

let sessionTimeoutId: ReturnType<typeof setTimeout> | null = null;

export const useSessionStore = create<SessionState>()(
  devtools(
    (set, get) => ({
      // Initial state
      user: null,
      isAuthenticated: false,
      isLoading: false,
      hasCheckedSession: false,
      error: null,
      lastFetchTime: 0,

      // Fetch session from API
      fetchSession: async (force = false) => {
        const state = get();
        const now = Date.now();
        const token = getSessionToken();

        if (!token) {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            hasCheckedSession: true,
            error: null,
            lastFetchTime: now,
          });
          console.log('[SessionStore] No session token found');
          return;
        }

        // Use cache if not forced and within cache duration
        if (!force && state.lastFetchTime && now - state.lastFetchTime < CACHE_DURATION) {
          console.log('[SessionStore] Using cached session');
          if (!state.hasCheckedSession) {
            set({ hasCheckedSession: true });
          }
          return;
        }

        // Prevent concurrent fetches
        if (state.isLoading) {
          console.log('[SessionStore] Already fetching, skipping...');
          return;
        }

        set({ isLoading: true, error: null });

        if (sessionTimeoutId) {
          clearTimeout(sessionTimeoutId);
        }
        sessionTimeoutId = setTimeout(() => {
          const current = get();
          if (current.isLoading) {
            set({
              isLoading: false,
              hasCheckedSession: true,
              error: new Error('Session check timed out'),
            });
          }
        }, 10000);

        try {
          if (!authAPIProvider) {
            throw new Error('Auth API not configured. Call setAuthAPI() first.');
          }
          const response = await Promise.race([
            authAPIProvider.getSession(),
            new Promise<APIResponse<SessionResponse>>((_, reject) => {
              setTimeout(() => reject(new Error('Session check timed out')), 8000);
            }),
          ]);

          if (response.success && response.data?.user) {
            set({
              user: response.data.user,
              isAuthenticated: true,
              isLoading: false,
              hasCheckedSession: true,
              error: null,
              lastFetchTime: now,
            });
            console.log('[SessionStore] Session fetched successfully');
          } else {
            // Not authenticated
            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
              hasCheckedSession: true,
              error: null,
              lastFetchTime: now,
            });
            console.log('[SessionStore] No active session');
          }
        } catch (error) {
          const err = error instanceof Error ? error : new Error('Failed to fetch session');
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            hasCheckedSession: true,
            error: err,
            lastFetchTime: 0,
          });
          console.error('[SessionStore] Fetch error:', err);
        } finally {
          if (sessionTimeoutId) {
            clearTimeout(sessionTimeoutId);
            sessionTimeoutId = null;
          }
        }
      },

      // Clear session (logout)
      clearSession: () => {
        removeSessionToken();
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          hasCheckedSession: false,
          error: null,
          lastFetchTime: 0,
        });
        console.log('[SessionStore] Session cleared');
      },

      // Set session manually (e.g., after login)
      setSession: (user: SessionUser | null) => {
        set({
          user,
          isAuthenticated: !!user,
          hasCheckedSession: true,
          lastFetchTime: Date.now(),
        });
      },

      // Internal setters (for DevTools visibility)
      _setLoading: (loading: boolean) => set({ isLoading: loading }),
      _setError: (error: Error | null) => set({ error }),
    }),
    { name: 'SessionStore' }
  )
);

/**
 * React Hook for session management
 *
 * Usage:
 *   const { user, isAuthenticated, isLoading, refresh, logout } = useSession()
 *
 * Features:
 * - Auto-fetches session on mount
 * - Auto-subscribes to changes
 * - Caches for 60 seconds
 * - Prevents redundant API calls
 */
export function useSession(options?: { autoFetch?: boolean }) {
  const { autoFetch = true } = options || {};

  const user = useSessionStore((state) => state.user);
  const isAuthenticated = useSessionStore((state) => state.isAuthenticated);
  const isLoading = useSessionStore((state) => state.isLoading);
  const hasCheckedSession = useSessionStore((state) => state.hasCheckedSession);
  const error = useSessionStore((state) => state.error);
  const fetchSession = useSessionStore((state) => state.fetchSession);
  const clearSession = useSessionStore((state) => state.clearSession);

  // Auto-fetch on mount
  useEffect(() => {
    if (autoFetch && !isLoading && !user) {
      fetchSession().catch(console.error);
    }
  }, [autoFetch, fetchSession, isLoading, user]);

  return {
    user,
    isAuthenticated,
    isLoading,
    hasCheckedSession,
    error,
    refresh: () => fetchSession(true),
    logout: clearSession,
  };
}

/**
 * Hook to get user info only
 */
export function useUser() {
  return useSessionStore((state) => state.user);
}

/**
 * Hook to check authentication status
 */
export function useIsAuthenticated() {
  return useSessionStore((state) => state.isAuthenticated);
}
